import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_URL } from '../../config';

function Frameworks() {
  const [frameworks, setFrameworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentFramework, setCurrentFramework] = useState(null);
  
  // Form states
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  
  useEffect(() => {
    fetchFrameworks();
  }, []);
  
  const fetchFrameworks = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/frameworks`);
      // Use mcpCount from backend, do not generate random counts
      setFrameworks(response.data.frameworks);
      setError('');
    } catch (err) {
      console.error('Error fetching frameworks:', err);
      setError('Failed to load frameworks');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddFramework = async () => {
    try {
      setError('');
      
      await axios.post(`${API_URL}/frameworks`, {
        name,
        isActive
      });
      
      setSuccess('Framework added successfully');
      setShowAddModal(false);
      
      // Reset form
      setName('');
      setIsActive(true);
      
      // Refresh frameworks list
      fetchFrameworks();
    } catch (err) {
      console.error('Error adding framework:', err);
      setError(err.response?.data?.message || 'Error adding framework');
    }
  };
  
  const handleEditFramework = async () => {
    try {
      setError('');
      
      await axios.put(`${API_URL}/frameworks`, {
        frameworkId: currentFramework.id,
        name,
        isActive
      });
      
      setSuccess('Framework updated successfully');
      setShowEditModal(false);
      
      // Refresh frameworks list
      fetchFrameworks();
    } catch (err) {
      console.error('Error updating framework:', err);
      setError(err.response?.data?.message || 'Error updating framework');
    }
  };
  
  const handleDeleteFramework = async () => {
    try {
      setError('');
      
      await axios.delete(`${API_URL}/frameworks/${currentFramework.id}`);
      
      setSuccess('Framework deleted successfully');
      setShowDeleteModal(false);
      
      // Refresh frameworks list
      fetchFrameworks();
    } catch (err) {
      console.error('Error deleting framework:', err);
      setError(err.response?.data?.message || 'Error deleting framework. It may be in use.');
    }
  };
  
  const openEditModal = (framework) => {
    setCurrentFramework(framework);
    setName(framework.name);
    setIsActive(framework.is_active === 1);
    setShowEditModal(true);
  };
  
  const openDeleteModal = (framework) => {
    setCurrentFramework(framework);
    setShowDeleteModal(true);
  };
  
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading frameworks...</p>
      </div>
    );
  }
  
  return (
    <>
      <Card className="shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="m-0">Frameworks</h5>
          <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Framework
          </Button>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          {frameworks.length === 0 ? (
            <p className="text-center">No frameworks found.</p>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>MCP Servers</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {frameworks.map(framework => (
                  <tr key={framework.id}>
                    <td>{framework.name}</td>
                    <td>
                      <span className={`badge bg-${framework.is_active ? 'success' : 'danger'}`}>
                        {framework.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{framework.mcpCount}</td>
                    <td>
                      <Button variant="info" size="sm" className="me-2" onClick={() => openEditModal(framework)}>
                        <FontAwesomeIcon icon={faEdit} /> Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => openDeleteModal(framework)}>
                        <FontAwesomeIcon icon={faTrash} /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      
      {/* Add Framework Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Framework</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Framework Name</Form.Label>
              <Form.Control 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="E.g., Magento"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check 
                type="checkbox" 
                label="Active" 
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                id="addFrameworkActive"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddFramework}>Add Framework</Button>
        </Modal.Footer>
      </Modal>
      
      {/* Edit Framework Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Framework</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Framework Name</Form.Label>
              <Form.Control 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check 
                type="checkbox" 
                label="Active" 
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                id="editFrameworkActive"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleEditFramework}>Update Framework</Button>
        </Modal.Footer>
      </Modal>
      
      {/* Delete Framework Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Framework</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentFramework && (
            <>
              <p>Are you sure you want to delete the framework "{currentFramework.name}"?</p>
              <p className="text-danger">Note: This action cannot be performed if the framework has associated MCP servers or is being used by users.</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteFramework}>Delete Framework</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Frameworks;
